import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Alert,
  CircularProgress
} from '@mui/material';
import { useAuth } from '../context/AuthContext';

const ChangePasswordPage: React.FC = () => {
  const { changePassword, user, passwordChangeRequired, logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const navigate = useNavigate();
  
  // Redirect to login if not authenticated
  React.useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate inputs
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      return;
    }
    
    try {
      setError('');
      setIsSubmitting(true);
      
      const response = await changePassword(currentPassword, newPassword);
      
      setSuccess(response.message || 'Password changed successfully');
      
      // If this was a required password change, redirect to dashboard after a short delay
      if (passwordChangeRequired) {
        setSuccess('Password changed successfully. You will be redirected to the dashboard.');
        setTimeout(() => {
          navigate('/admin/dashboard');
        }, 2000);
      }
    } catch (err: any) {
      console.error('Password change error:', err);
      
      if (err.response) {
        if (err.response.status === 401) {
          setError('Current password is incorrect');
        } else if (err.response.data?.message) {
          setError(err.response.data.message);
        } else {
          setError(`Server error: ${err.response.status}`);
        }
      } else if (err.request) {
        setError('No response from server. Please try again later.');
      } else {
        setError(err.message || 'An unexpected error occurred');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Paper 
        elevation={3} 
        sx={{ 
          mt: 8, 
          p: 4, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center' 
        }}
      >
        <Typography component="h1" variant="h5">
          {passwordChangeRequired ? 'Password Change Required' : 'Change Password'}
        </Typography>
        
        {passwordChangeRequired && (
          <Alert severity="warning" sx={{ mt: 2, width: '100%' }}>
            For security reasons, you must change your password before continuing.
            You will not be able to access the system until you change your password.
          </Alert>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mt: 2, width: '100%' }}>
            {success}
          </Alert>
        )}
        
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3, width: '100%' }}>
          <TextField
            margin="normal"
            required
            fullWidth
            name="currentPassword"
            label="Current Password"
            type="password"
            id="currentPassword"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            disabled={isSubmitting}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="newPassword"
            label="New Password"
            type="password"
            id="newPassword"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={isSubmitting}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label="Confirm New Password"
            type="password"
            id="confirmPassword"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={isSubmitting}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={24} /> : 'Change Password'}
          </Button>
          
          {!passwordChangeRequired ? (
            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate('/admin/dashboard')}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          ) : (
            <Button
              fullWidth
              variant="outlined"
              onClick={() => logout(false)}
              disabled={isSubmitting}
              sx={{ mt: 2 }}
            >
              Logout
            </Button>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default ChangePasswordPage;
